// TODO: Consolidate with duplicate Banking component in AdvisorPortal domain
import React, { useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line, Pie } from "react-chartjs-2";
import SubPageLayout from "../../components/shared/SubPageLayout";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
);

const TabButton = ({ active, onClick, emoji, label }) => {
  const buttonClass = active
    ? "border-blue-500 text-blue-600"
    : "border-transparent text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:text-gray-100 hover:border-gray-300";

  return (
    <button
      className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${buttonClass}`}
      onClick={onClick}
    >
      <span className="mr-2">{emoji}</span>
      {label}
    </button>
  );
};

const TabNavigation = ({ activeTab, setActiveTab }) => (
  <div className="border-b border-gray-200 dark:border-gray-700 mb-8">
    <nav className="flex space-x-8">
      <TabButton
        active={activeTab === "dashboard"}
        onClick={() => setActiveTab("dashboard")}
        emoji="📊"
        label="Dashboard"
      />
      <TabButton
        active={activeTab === "spending"}
        onClick={() => setActiveTab("spending")}
        emoji="💰"
        label="Spending Analysis"
      />
      <TabButton
        active={activeTab === "accounts"}
        onClick={() => setActiveTab("accounts")}
        emoji="💳"
        label="Accounts"
      />
      <TabButton
        active={activeTab === "security"}
        onClick={() => setActiveTab("security")}
        emoji="🔒"
        label="Security"
      />
      <TabButton
        active={activeTab === "clients"}
        onClick={() => setActiveTab("clients")}
        emoji="👥"
        label="Client Management"
      />
    </nav>
  </div>
);

const StatCard = ({ title, value, emoji, textColor }) => (
  <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-md dark:shadow-none">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
      <span className={`text-2xl ${textColor}`}>{emoji}</span>
    </div>
    <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
  </div>
);

const StatisticsPanel = ({ statistics }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
    <StatCard
      title="Connected Clients"
      value={statistics.totalConnectedClients}
      emoji="👥"
      textColor="text-blue-500"
    />
    <StatCard
      title="Connected Accounts"
      value={statistics.totalConnectedAccounts}
      emoji="🔗"
      textColor="text-green-500"
    />
    <StatCard
      title="Pending Invites"
      value={statistics.pendingInvites}
      emoji="🔔"
      textColor="text-yellow-500"
    />
    <StatCard
      title="Connection Rate"
      value={`${statistics.connectionPercentage}%`}
      emoji="📈"
      textColor="text-purple-500"
    />
  </div>
);

const Leaderboard = ({ data }) => (
  <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow-md dark:shadow-none p-6 mb-8">
    <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
      Advisor Leaderboard
    </h2>
    <div className="overflow-x-auto">
      <table className="min-w-full">
        <thead>
          <tr className="bg-gray-50">
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Advisor
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Connected Accounts
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Connection Rate
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Avg. Connection Time
            </th>
          </tr>
        </thead>
        <tbody className="bg-surface-default dark:bg-surface-default divide-y divide-gray-200">
          {data.map((advisor, index) => (
            <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
              <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-gray-100">
                {advisor.name}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-gray-100">
                {advisor.connectedAccounts}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-gray-100">
                {advisor.connectionRate}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-gray-100">
                {advisor.avgConnectionTime}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const ActionButtons = () => (
  <div className="flex flex-wrap gap-4 mb-8">
    <button className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors">
      Send Reminder
    </button>
    <button className="bg-green-500 text-white px-6 py-2 rounded-lg hover:bg-green-600 transition-colors">
      Generate Connection Link
    </button>
    <button className="bg-purple-500 text-white px-6 py-2 rounded-lg hover:bg-purple-600 transition-colors">
      View Reports
    </button>
  </div>
);

const ClientSearch = () => (
  <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-md dark:shadow-none mb-8">
    <div className="max-w-xl">
      <label
        htmlFor="search"
        className="block text-sm font-medium text-gray-900 dark:text-gray-100"
      >
        Search Clients
      </label>
      <div className="mt-1 relative rounded-md shadow-sm">
        <input
          type="text"
          name="search"
          id="search"
          className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-4 pr-12 sm:text-sm border-gray-300 rounded-md text-gray-900 dark:text-gray-100"
          placeholder="Search by name, email, or household ID"
        />
      </div>
    </div>
  </div>
);

const InsightsPanel = ({ spendingData, categoryData }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
    <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-md dark:shadow-none">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
        Spending Trends
      </h3>
      <Line data={spendingData} options={{ responsive: true }} />
    </div>
    <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-md dark:shadow-none">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
        Spending by Category
      </h3>
      <Pie data={categoryData} options={{ responsive: true }} />
    </div>
  </div>
);

const ValueProposition = () => (
  <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-md dark:shadow-none mb-8">
    <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
      Why Connect Banking?
    </h2>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="p-4 border rounded-lg">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Real-time Visibility
        </h3>
        <p className="text-gray-700 dark:text-gray-300">
          Get instant access to your clients' financial health and transaction
          data.
        </p>
      </div>
      <div className="p-4 border rounded-lg">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Better Insights</h3>
        <p className="text-gray-700 dark:text-gray-300">
          Understand spending patterns and make informed recommendations.
        </p>
      </div>
      <div className="p-4 border rounded-lg">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Simplified Planning
        </h3>
        <p className="text-gray-700 dark:text-gray-300">
          Streamline budgeting and tax preparation processes.
        </p>
      </div>
    </div>
  </div>
);

const ClientBankingPanel = ({ accounts }) => (
  <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-md dark:shadow-none mb-8">
    <div className="flex justify-between items-center mb-6">
      <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">Connected Accounts</h3>
      <button className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors">
        Add Account
      </button>
    </div>
    <div className="overflow-x-auto">
      <table className="min-w-full">
        <thead>
          <tr className="bg-gray-50">
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Account Name
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Bank
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Balance
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-surface-default dark:bg-surface-default divide-y divide-gray-200">
          {accounts.map((account, index) => (
            <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
              <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-gray-100">
                {account.name}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-gray-100">
                {account.bank}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-gray-100">
                ${account.balance.toLocaleString()}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`px-2 py-1 text-xs font-medium rounded-full ${
                    account.status === "Active"
                      ? "bg-green-100 text-green-800"
                      : account.status === "Error"
                        ? "bg-red-100 text-red-800"
                        : "bg-yellow-100 text-yellow-800"
                  }`}
                >
                  {account.status}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <button className="text-blue-600 hover:text-blue-800 mr-3">
                  Reconnect
                </button>
                <button className="text-red-600 hover:text-red-800">
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const ClientTable = ({ clients }) => (
  <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow-md dark:shadow-none p-6 mb-8">
    <div className="overflow-x-auto">
      <table className="min-w-full">
        <thead>
          <tr className="bg-gray-50">
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 dark:text-gray-100 uppercase tracking-wider">
              Client Name
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 dark:text-gray-100 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 dark:text-gray-100 uppercase tracking-wider">
              Connected Accounts
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 dark:text-gray-100 uppercase tracking-wider">
              Last Activity
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 dark:text-gray-100 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-surface-default dark:bg-surface-default divide-y divide-gray-200">
          {clients.map((client, index) => (
            <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
              <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-gray-100">
                {client.name}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`px-2 py-1 text-xs font-medium rounded-full ${
                    client.status === "Connected"
                      ? "bg-green-100 text-gray-900 dark:text-gray-100"
                      : client.status === "Pending"
                        ? "bg-yellow-100 text-gray-900 dark:text-gray-100"
                        : "bg-red-100 text-gray-900 dark:text-gray-100"
                  }`}
                >
                  {client.status}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-gray-100">
                {client.connectedAccounts}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-gray-100">
                {client.lastActivity}
              </td>
              <td className="px-6 py-4 whitespace-nowrap space-x-2">
                <button className="text-gray-900 dark:text-gray-100 hover:text-blue-800">
                  View Details
                </button>
                <button className="text-gray-900 dark:text-gray-100 hover:text-green-800">
                  Send Invite
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const RecurringExpenses = ({ expenses }) => (
  <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-md dark:shadow-none mb-8">
    <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-6">Recurring Expenses</h3>
    <div className="overflow-x-auto">
      <table className="min-w-full">
        <thead>
          <tr className="bg-gray-50">
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Vendor
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Frequency
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Amount
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Category
            </th>
          </tr>
        </thead>
        <tbody className="bg-surface-default dark:bg-surface-default divide-y divide-gray-200">
          {expenses.map((expense, index) => (
            <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
              <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-gray-100">
                {expense.vendor}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-gray-100">
                {expense.frequency}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-gray-100">
                ${expense.amount}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-gray-100">
                {expense.category}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const CreditScoreSpeedometer = ({ score }) => {
  // Calculate the rotation angle based on credit score (300-850 range)
  const minScore = 300;
  const maxScore = 850;
  const range = maxScore - minScore;
  const normalizedScore = score - minScore;
  const rotation = (normalizedScore / range) * 270; // 270 degrees for Q-style arc

  return (
    <div className="relative w-64 h-64 mx-auto">
      {/* Outer circle background */}
      <div className="absolute inset-0 rounded-full border-[16px] border-gray-100 dark:border-gray-800" />

      {/* Score arc background - using border trick for clean arc */}
      <div className="absolute inset-0 rounded-full border-[16px] border-t-transparent border-r-transparent border-b-transparent border-l-gray-200 transform -rotate-135" />

      {/* Score arc - dynamic based on score */}
      <div
        className="absolute inset-0 rounded-full border-[16px] border-t-transparent border-r-transparent border-b-transparent border-l-blue-500 transform -rotate-135 transition-transform duration-500"
        style={{ transform: `rotate(${rotation - 135}deg)` }}
      />

      {/* Center content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-4xl font-bold text-gray-900 dark:text-gray-100">{score}</div>
        <div className="text-sm text-gray-600 dark:text-gray-400 mt-2">Credit Score</div>
      </div>

      {/* Score range labels */}
      <div className="absolute inset-0">
        <span className="absolute top-1/2 left-0 transform -translate-y-1/2 -translate-x-8 text-sm text-gray-600 dark:text-gray-400">
          300
        </span>
        <span className="absolute bottom-0 left-1/2 transform translate-y-6 -translate-x-1/2 text-sm text-gray-600 dark:text-gray-400">
          575
        </span>
        <span className="absolute top-1/2 right-0 transform -translate-y-1/2 translate-x-8 text-sm text-gray-600 dark:text-gray-400">
          850
        </span>
      </div>
    </div>
  );
};

const CreditMonitoring = ({ creditScore, trends }) => {
  const [selectedAgency, setSelectedAgency] = useState("all");

  const agencies = {
    equifax: { score: creditScore, change: trends.change },
    transunion: { score: creditScore - 5, change: trends.change - 2 },
    experian: { score: creditScore + 3, change: trends.change + 1 },
  };

  const getScoreColor = (score) => {
    if (score >= 740) return "text-green-600";
    if (score >= 670) return "text-yellow-600";
    return "text-red-600";
  };

  const AllAgenciesView = () => (
    <div className="grid grid-cols-3 gap-6">
      {Object.entries(agencies).map(([agency, data]) => (
        <div
          key={agency}
          className="p-6 border rounded-lg bg-surface-default dark:bg-surface-default hover:shadow-lg dark:shadow-none transition-shadow"
        >
          <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 capitalize">
            {agency}
          </h4>
          <div
            className={`text-4xl font-bold mb-3 ${getScoreColor(data.score)}`}
          >
            {data.score}
          </div>
          <div
            className={`text-sm font-medium ${data.change >= 0 ? "text-green-600" : "text-red-600"}`}
          >
            {data.change > 0 ? "+" : ""}
            {data.change} points
          </div>
          <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">Last 30 days</div>
        </div>
      ))}
    </div>
  );

  const SingleAgencyView = ({ agency, data }) => (
    <div className="p-6 border rounded-lg">
      <h4 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6 text-center capitalize">
        {agency}
      </h4>
      <CreditScoreSpeedometer score={data.score} />
      <div className="mt-6 text-center">
        <div
          className={`text-lg font-semibold ${data.change >= 0 ? "text-green-600" : "text-red-600"}`}
        >
          {data.change > 0 ? "+" : ""}
          {data.change} points
        </div>
        <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">Last 30 days</p>
      </div>
    </div>
  );

  return (
    <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-md dark:shadow-none mb-8">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">Credit Monitoring</h3>
        <div className="flex space-x-2">
          <button
            onClick={() => setSelectedAgency("all")}
            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
              selectedAgency === "all"
                ? "bg-blue-100 text-blue-800"
                : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
            }`}
          >
            All Agencies
          </button>
          {Object.keys(agencies).map((agency) => (
            <button
              key={agency}
              onClick={() => setSelectedAgency(agency)}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                selectedAgency === agency
                  ? "bg-blue-100 text-blue-800"
                  : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
              }`}
            >
              {agency.charAt(0).toUpperCase() + agency.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-8">
        {selectedAgency === "all" ? (
          <AllAgenciesView />
        ) : (
          <SingleAgencyView
            agency={selectedAgency}
            data={agencies[selectedAgency]}
          />
        )}
      </div>

      <div className="p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-gray-100">Score Factors</h4>
            <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
              Last updated {trends.lastUpdate}
            </p>
          </div>
          <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
            View Full Report →
          </button>
        </div>
      </div>
    </div>
  );
};

const ClientSegmentation = () => (
  <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-md dark:shadow-none mb-8">
    <div className="flex justify-between items-center mb-6">
      <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">Client Segmentation</h3>
      <select className="border-gray-300 rounded-md text-gray-700 dark:text-gray-300 text-sm">
        <option>By Age Group</option>
        <option>By Net Worth</option>
        <option>By Engagement Level</option>
      </select>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="p-4 border rounded-lg bg-blue-50">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Age 25-40</span>
          <span className="text-lg font-bold text-blue-600">32%</span>
        </div>
        <div className="text-sm text-gray-600 dark:text-gray-400">45 clients</div>
        <div className="mt-2 text-sm text-blue-700">28 connected</div>
      </div>
      <div className="p-4 border rounded-lg bg-green-50">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Age 41-55</span>
          <span className="text-lg font-bold text-green-600">45%</span>
        </div>
        <div className="text-sm text-gray-600 dark:text-gray-400">63 clients</div>
        <div className="mt-2 text-sm text-green-700">52 connected</div>
      </div>
      <div className="p-4 border rounded-lg bg-purple-50">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Age 56+</span>
          <span className="text-lg font-bold text-purple-600">23%</span>
        </div>
        <div className="text-sm text-gray-600 dark:text-gray-400">32 clients</div>
        <div className="mt-2 text-sm text-purple-700">24 connected</div>
      </div>
    </div>
  </div>
);

const AdvisoryTips = () => (
  <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-md dark:shadow-none mb-8">
    <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">Advisory Insights</h3>
    <div className="space-y-4">
      <div className="flex items-start space-x-4 p-4 bg-blue-50 rounded-lg">
        <span className="text-2xl">💡</span>
        <div>
          <h4 className="font-semibold text-gray-900 dark:text-gray-100">Priority Focus Needed</h4>
          <p className="text-gray-700 dark:text-gray-300">
            30% of clients aged 40-50 haven't connected their accounts yet.
          </p>
          <button className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium">
            View Client List →
          </button>
        </div>
      </div>
      <div className="flex items-start space-x-4 p-4 bg-green-50 rounded-lg">
        <span className="text-2xl">📈</span>
        <div>
          <h4 className="font-semibold text-gray-900 dark:text-gray-100">Growth Opportunity</h4>
          <p className="text-gray-700 dark:text-gray-300">
            15 clients have additional accounts ready to connect.
          </p>
          <button className="mt-2 text-green-600 hover:text-green-800 text-sm font-medium">
            Send Invites →
          </button>
        </div>
      </div>
      <div className="flex items-start space-x-4 p-4 bg-yellow-50 rounded-lg">
        <span className="text-2xl">⚡</span>
        <div>
          <h4 className="font-semibold text-gray-900 dark:text-gray-100">Quick Win</h4>
          <p className="text-gray-700 dark:text-gray-300">
            5 clients have pending invites older than 7 days.
          </p>
          <button className="mt-2 text-yellow-600 hover:text-yellow-800 text-sm font-medium">
            Send Reminders →
          </button>
        </div>
      </div>
    </div>
  </div>
);

const PriorityClients = () => (
  <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-md dark:shadow-none mb-8">
    <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
      Priority Follow-ups
    </h3>
    <div className="space-y-4">
      {[
        {
          name: "Michael Brown",
          reason: "Disconnected account",
          urgency: "High",
          lastContact: "5 days ago",
        },
        {
          name: "Emma Wilson",
          reason: "Pending invite",
          urgency: "Medium",
          lastContact: "3 days ago",
        },
        {
          name: "James Lee",
          reason: "New client",
          urgency: "Medium",
          lastContact: "1 week ago",
        },
      ].map((client, index) => (
        <div
          key={index}
          className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          <div className="flex items-center space-x-4">
            <div
              className={`w-2 h-2 rounded-full ${
                client.urgency === "High"
                  ? "bg-red-500"
                  : client.urgency === "Medium"
                    ? "bg-yellow-500"
                    : "bg-green-500"
              }`}
            />
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">{client.name}</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">{client.reason}</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-500 dark:text-gray-500">
              Last contact: {client.lastContact}
            </span>
            <button className="text-blue-600 hover:text-blue-800">
              Contact →
            </button>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const SpendingTrends = ({ spendingData }) => {
  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Monthly Spending Trends",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => `$${value.toLocaleString()}`,
        },
      },
    },
  };

  return (
    <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-md dark:shadow-none">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Spending Trends</h3>
        <select className="border-gray-300 rounded-md text-gray-700 dark:text-gray-300 text-sm">
          <option>Last 6 Months</option>
          <option>Last Year</option>
          <option>YTD</option>
        </select>
      </div>
      <Line data={spendingData} options={options} />
      <div className="mt-4 p-4 bg-blue-50 rounded-lg">
        <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Predictive Insights
        </h4>
        <p className="text-gray-700 dark:text-gray-300">
          Based on current trends, projected spending for next month: $3,800
        </p>
        <p className="text-gray-700 dark:text-gray-300 mt-1">
          Recommendation: Consider reducing entertainment expenses by 15%
        </p>
      </div>
    </div>
  );
};

const CategoryManager = ({ categoryData }) => {
  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: "right",
      },
    },
  };

  return (
    <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-md dark:shadow-none">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Spending by Category
        </h3>
        <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
          Customize Categories
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Pie data={categoryData} options={options} />
        </div>
        <div className="space-y-4">
          {categoryData.labels.map((label, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-2 border rounded-lg"
            >
              <div className="flex items-center space-x-2">
                <div
                  className="w-4 h-4 rounded-full"
                  style={{
                    backgroundColor:
                      categoryData.datasets[0].backgroundColor[index],
                  }}
                />
                <span className="text-gray-900 dark:text-gray-100">{label}</span>
              </div>
              <div className="text-gray-900 dark:text-gray-100 font-medium">
                ${(categoryData.datasets[0].data[index] * 100).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const BudgetTracker = () => (
  <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-md dark:shadow-none mb-8">
    <div className="flex justify-between items-center mb-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Budget Progress</h3>
      <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
        Adjust Budgets
      </button>
    </div>
    <div className="space-y-4">
      {[
        { category: "Housing", spent: 2000, budget: 2500, color: "blue" },
        {
          category: "Transportation",
          spent: 800,
          budget: 1000,
          color: "green",
        },
        { category: "Food", spent: 600, budget: 800, color: "yellow" },
        { category: "Entertainment", spent: 400, budget: 300, color: "red" },
      ].map((item, index) => (
        <div key={index} className="space-y-2">
          <div className="flex justify-between text-sm text-gray-900 dark:text-gray-100">
            <span>{item.category}</span>
            <span>
              ${item.spent} of ${item.budget}
            </span>
          </div>
          <div className="relative pt-1">
            <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200">
              <div
                style={{ width: `${(item.spent / item.budget) * 100}%` }}
                className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-${item.color}-500`}
              />
            </div>
          </div>
          {item.spent > item.budget && (
            <p className="text-red-600 text-sm">
              Over budget by ${item.spent - item.budget}
            </p>
          )}
        </div>
      ))}
    </div>
  </div>
);

const SpendingAlerts = () => (
  <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-md dark:shadow-none mb-8">
    <div className="flex justify-between items-center mb-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Spending Alerts</h3>
      <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
        Configure Alerts
      </button>
    </div>
    <div className="space-y-4">
      <div className="p-4 border rounded-lg bg-red-50">
        <div className="flex items-start space-x-3">
          <span className="text-red-500 text-xl">⚠️</span>
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-gray-100">
              Entertainment Over Budget
            </h4>
            <p className="text-gray-700 dark:text-gray-300">
              Exceeded monthly budget by $100 (33% over)
            </p>
            <button className="mt-2 text-red-600 hover:text-red-800 text-sm font-medium">
              View Transactions →
            </button>
          </div>
        </div>
      </div>
      <div className="p-4 border rounded-lg bg-yellow-50">
        <div className="flex items-start space-x-3">
          <span className="text-yellow-500 text-xl">⚠️</span>
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-gray-100">Unusual Activity</h4>
            <p className="text-gray-700 dark:text-gray-300">
              Large transaction detected: $500 at Amazon
            </p>
            <button className="mt-2 text-yellow-600 hover:text-yellow-800 text-sm font-medium">
              Review Transaction →
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const AccountHealthScore = ({ accounts }) => {
  const totalAccounts = accounts.length;
  const activeAccounts = accounts.filter(
    (acc) => acc.status === "Active",
  ).length;
  const healthScore = Math.round((activeAccounts / totalAccounts) * 100);

  return (
    <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-md dark:shadow-none mb-8">
      <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-6">
        Account Health Overview
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="p-4 border rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold text-gray-900 dark:text-gray-100">
              Overall Health Score
            </h4>
            <span
              className={`text-lg font-bold ${
                healthScore >= 90
                  ? "text-green-600"
                  : healthScore >= 70
                    ? "text-yellow-600"
                    : "text-red-600"
              }`}
            >
              {healthScore}%
            </span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full ${
                healthScore >= 90
                  ? "bg-green-500"
                  : healthScore >= 70
                    ? "bg-yellow-500"
                    : "bg-red-500"
              }`}
              style={{ width: `${healthScore}%` }}
            />
          </div>
        </div>
        <div className="p-4 border rounded-lg">
          <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Connection Status
          </h4>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-700 dark:text-gray-300">Active</span>
              <span className="text-green-600 font-medium">
                {activeAccounts}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-700 dark:text-gray-300">Issues</span>
              <span className="text-red-600 font-medium">
                {totalAccounts - activeAccounts}
              </span>
            </div>
          </div>
        </div>
        <div className="p-4 border rounded-lg">
          <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Last Sync Status</h4>
          <div className="text-sm text-gray-700 dark:text-gray-300">
            Last successful sync: 5 mins ago
          </div>
          <div className="text-sm text-green-600 mt-1">
            All systems operational
          </div>
        </div>
      </div>
    </div>
  );
};

const AccountActions = () => (
  <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-md dark:shadow-none mb-8">
    <div className="flex justify-between items-center mb-6">
      <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">Quick Actions</h3>
      <button className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors">
        Bulk Actions
      </button>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <button className="p-4 border rounded-lg hover:bg-blue-50 transition-colors group">
        <div className="flex items-center space-x-3">
          <span className="text-2xl">🔄</span>
          <div className="text-left">
            <h4 className="font-semibold text-gray-900 dark:text-gray-100">Sync All</h4>
            <p className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:text-gray-100">
              Update all account data
            </p>
          </div>
        </div>
      </button>
      <button className="p-4 border rounded-lg hover:bg-blue-50 transition-colors group">
        <div className="flex items-center space-x-3">
          <span className="text-2xl">➕</span>
          <div className="text-left">
            <h4 className="font-semibold text-gray-900 dark:text-gray-100">Add Account</h4>
            <p className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:text-gray-100">
              Connect new account
            </p>
          </div>
        </div>
      </button>
      <button className="p-4 border rounded-lg hover:bg-blue-50 transition-colors group">
        <div className="flex items-center space-x-3">
          <span className="text-2xl">⚡</span>
          <div className="text-left">
            <h4 className="font-semibold text-gray-900 dark:text-gray-100">Fix Issues</h4>
            <p className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:text-gray-100">
              Resolve connection problems
            </p>
          </div>
        </div>
      </button>
    </div>
  </div>
);

const ConnectionQuality = ({ accounts }) => (
  <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-md dark:shadow-none mb-8">
    <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-6">Connection Quality</h3>
    <div className="space-y-6">
      {accounts.map((account, index) => (
        <div key={index} className="border rounded-lg p-4">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">{account.name}</h4>
              <p className="text-sm text-gray-700 dark:text-gray-300">{account.bank}</p>
            </div>
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                account.status === "Active"
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-800"
              }`}
            >
              {account.status}
            </span>
          </div>
          <div className="space-y-3">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-700 dark:text-gray-300">Connection Stability</span>
                <span className="text-gray-900 dark:text-gray-100">98%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-green-500" style={{ width: "98%" }} />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-700 dark:text-gray-300">Data Quality</span>
                <span className="text-gray-900 dark:text-gray-100">100%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-blue-500" style={{ width: "100%" }} />
              </div>
            </div>
            <div className="text-sm text-gray-700 dark:text-gray-300">
              Last Error: {account.status === "Active" ? "None" : "2 hours ago"}
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const SecurityStatus = () => (
  <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-md dark:shadow-none mb-8">
    <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-6">Security Overview</h3>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="p-4 border rounded-lg bg-green-50">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-gray-900 dark:text-gray-100">Overall Status</h4>
          <span className="px-2 py-1 text-sm font-medium bg-green-100 text-green-800 rounded-full">
            Secure
          </span>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          All security measures are up to date
        </p>
      </div>
      <div className="p-4 border rounded-lg bg-blue-50">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-gray-900 dark:text-gray-100">Last Security Audit</h4>
          <span className="text-blue-600">24h ago</span>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">No issues found in last scan</p>
      </div>
      <div className="p-4 border rounded-lg bg-purple-50">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-gray-900 dark:text-gray-100">Active Sessions</h4>
          <span className="text-purple-600">3 devices</span>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">All sessions verified</p>
      </div>
    </div>
  </div>
);

const SecurityActivity = () => (
  <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-md dark:shadow-none mb-8">
    <div className="flex justify-between items-center mb-6">
      <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">Recent Activity</h3>
      <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
        View All Activity →
      </button>
    </div>
    <div className="space-y-4">
      {[
        {
          type: "Login",
          device: "Windows PC",
          location: "New York, US",
          time: "2 minutes ago",
          status: "success",
        },
        {
          type: "Password Change",
          device: "iPhone 12",
          location: "New York, US",
          time: "2 days ago",
          status: "success",
        },
        {
          type: "Failed Login",
          device: "Unknown Device",
          location: "Beijing, CN",
          time: "3 days ago",
          status: "blocked",
        },
      ].map((activity, index) => (
        <div
          key={index}
          className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          <div className="flex items-center space-x-4">
            <span
              className={`w-2 h-2 rounded-full ${
                activity.status === "success" ? "bg-green-500" : "bg-red-500"
              }`}
            />
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">{activity.type}</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {activity.device} • {activity.location}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-500 dark:text-gray-500">{activity.time}</span>
            <button className="text-blue-600 hover:text-blue-800">
              Details
            </button>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const SecuritySettings = () => (
  <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-md dark:shadow-none mb-8">
    <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-6">Security Settings</h3>
    <div className="space-y-6">
      <div className="flex items-center justify-between p-4 border rounded-lg">
        <div>
          <h4 className="font-semibold text-gray-900 dark:text-gray-100">
            Two-Factor Authentication
          </h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Add an extra layer of security to your account
          </p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input type="checkbox" className="sr-only peer" checked />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-surface-default dark:bg-surface-default after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
        </label>
      </div>
      <div className="flex items-center justify-between p-4 border rounded-lg">
        <div>
          <h4 className="font-semibold text-gray-900 dark:text-gray-100">Login Notifications</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Get notified of new device logins
          </p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input type="checkbox" className="sr-only peer" checked />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-surface-default dark:bg-surface-default after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
        </label>
      </div>
      <div className="flex items-center justify-between p-4 border rounded-lg">
        <div>
          <h4 className="font-semibold text-gray-900 dark:text-gray-100">IP Whitelisting</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Restrict access to trusted IP addresses
          </p>
        </div>
        <button className="text-blue-600 hover:text-blue-800">Configure</button>
      </div>
      <div className="flex items-center justify-between p-4 border rounded-lg">
        <div>
          <h4 className="font-semibold text-gray-900 dark:text-gray-100">Session Timeout</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Automatically log out after inactivity
          </p>
        </div>
        <select className="border-gray-300 rounded-md text-gray-700 dark:text-gray-300 text-sm">
          <option>15 minutes</option>
          <option>30 minutes</option>
          <option>1 hour</option>
          <option>4 hours</option>
        </select>
      </div>
    </div>
  </div>
);

const AccessControl = () => (
  <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-md dark:shadow-none mb-8">
    <div className="flex justify-between items-center mb-6">
      <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">Access Control</h3>
      <button className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors">
        Add User
      </button>
    </div>
    <div className="overflow-x-auto">
      <table className="min-w-full">
        <thead>
          <tr className="bg-gray-50">
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              User
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Role
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Access Level
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Last Access
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-surface-default dark:bg-surface-default divide-y divide-gray-200">
          {[
            {
              name: "John Doe",
              role: "Admin",
              accessLevel: "Full Access",
              lastAccess: "2 hours ago",
            },
            {
              name: "Jane Smith",
              role: "Manager",
              accessLevel: "Limited Access",
              lastAccess: "1 day ago",
            },
            {
              name: "Bob Johnson",
              role: "Viewer",
              accessLevel: "Read Only",
              lastAccess: "5 days ago",
            },
          ].map((user, index) => (
            <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
              <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-gray-100">
                {user.name}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-gray-100">
                {user.role}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`px-2 py-1 text-xs font-medium rounded-full ${
                    user.accessLevel === "Full Access"
                      ? "bg-green-100 text-green-800"
                      : user.accessLevel === "Limited Access"
                        ? "bg-yellow-100 text-yellow-800"
                        : "bg-gray-100 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  {user.accessLevel}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-gray-100">
                {user.lastAccess}
              </td>
              <td className="px-6 py-4 whitespace-nowrap space-x-2">
                <button className="text-blue-600 hover:text-blue-800">
                  Edit
                </button>
                <button className="text-red-600 hover:text-red-800">
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const ClientFilters = () => (
  <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-md dark:shadow-none mb-8">
    <div className="flex flex-wrap gap-4 items-center">
      <div className="flex-1 min-w-[200px]">
        <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
          Status
        </label>
        <select className="w-full border-gray-300 rounded-md text-gray-900 dark:text-gray-100">
          <option value="">All Statuses</option>
          <option value="connected">Connected</option>
          <option value="pending">Pending</option>
          <option value="not_connected">Not Connected</option>
        </select>
      </div>
      <div className="flex-1 min-w-[200px]">
        <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
          Sort By
        </label>
        <select className="w-full border-gray-300 rounded-md text-gray-900 dark:text-gray-100">
          <option value="name">Name</option>
          <option value="last_activity">Last Activity</option>
          <option value="accounts">Connected Accounts</option>
        </select>
      </div>
      <div className="flex-1 min-w-[200px]">
        <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
          Connection Type
        </label>
        <select className="w-full border-gray-300 rounded-md text-gray-900 dark:text-gray-100">
          <option value="">All Types</option>
          <option value="bank">Banking</option>
          <option value="investment">Investment</option>
          <option value="credit">Credit Cards</option>
        </select>
      </div>
      <button className="bg-gray-100 text-gray-900 dark:text-gray-100 px-4 py-2 rounded-md hover:bg-gray-200 mt-auto">
        Reset Filters
      </button>
    </div>
  </div>
);

const ClientStats = ({ stats }) => (
  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
    {[
      { label: "Total Clients", value: stats.total, color: "gray-900" },
      { label: "Connected", value: stats.connected, color: "gray-900" },
      { label: "Pending", value: stats.pending, color: "gray-900" },
      { label: "Not Connected", value: stats.notConnected, color: "gray-900" },
    ].map((stat, index) => (
      <div key={index} className="bg-surface-default dark:bg-surface-default p-4 rounded-lg shadow-md dark:shadow-none">
        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">{stat.label}</h4>
        <div className={`mt-2 text-2xl font-bold text-${stat.color}`}>
          {stat.value}
        </div>
        <div className="mt-1 text-sm text-gray-900 dark:text-gray-100">
          {Math.round((stat.value / stats.total) * 100)}% of total
        </div>
      </div>
    ))}
  </div>
);

const BulkActions = () => (
  <div className="bg-surface-default dark:bg-surface-default p-4 rounded-lg shadow-md dark:shadow-none mb-8 flex items-center justify-between">
    <div className="flex items-center space-x-2">
      <input
        type="checkbox"
        className="rounded border-gray-300 text-blue-600"
      />
      <span className="text-sm text-gray-900 dark:text-gray-100">Select All</span>
    </div>
    <div className="flex space-x-2">
      <button className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors">
        Bulk Invite
      </button>
      <button className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors">
        Export Selected
      </button>
      <button className="bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 transition-colors">
        Generate Report
      </button>
    </div>
  </div>
);

const ClientDetails = ({ client }) => (
  <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-md dark:shadow-none mb-8">
    <div className="flex justify-between items-start mb-6">
      <div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">{client.name}</h3>
        <p className="text-gray-900 dark:text-gray-100">Client ID: {client.id}</p>
      </div>
      <span
        className={`px-3 py-1 rounded-full text-sm font-medium ${
          client.status === "Connected"
            ? "bg-green-100 text-gray-900 dark:text-gray-100"
            : client.status === "Pending"
              ? "bg-yellow-100 text-gray-900 dark:text-gray-100"
              : "bg-red-100 text-gray-900 dark:text-gray-100"
        }`}
      >
        {client.status}
      </span>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
      <div>
        <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Contact Information</h4>
        <div className="space-y-1 text-sm text-gray-900 dark:text-gray-100">
          <p>📧 {client.email}</p>
          <p>📱 {client.phone}</p>
          <p>🏠 {client.address}</p>
        </div>
      </div>
      <div>
        <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Account Summary</h4>
        <div className="space-y-1 text-sm text-gray-900 dark:text-gray-100">
          <p>Connected Accounts: {client.connectedAccounts}</p>
          <p>Total Balance: ${client.totalBalance?.toLocaleString()}</p>
          <p>Last Activity: {client.lastActivity}</p>
        </div>
      </div>
      <div>
        <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Quick Actions</h4>
        <div className="flex flex-wrap gap-2">
          <button className="text-gray-900 dark:text-gray-100 hover:text-blue-800">
            Send Message
          </button>
          <button className="text-gray-900 dark:text-gray-100 hover:text-green-800">
            Schedule Meeting
          </button>
          <button className="text-gray-900 dark:text-gray-100 hover:text-purple-800">
            View History
          </button>
        </div>
      </div>
    </div>

    <div className="border-t pt-6">
      <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-4">Recent Activity</h4>
      <div className="space-y-3">
        {client.recentActivity?.map((activity, index) => (
          <div
            key={index}
            className="flex items-center justify-between text-sm"
          >
            <div className="flex items-center space-x-2">
              <span className="w-2 h-2 rounded-full bg-blue-500"></span>
              <span className="text-gray-900 dark:text-gray-100">{activity.action}</span>
            </div>
            <span className="text-gray-900 dark:text-gray-100">{activity.date}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const Banking = () => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [statistics] = useState({
    totalConnectedClients: 156,
    totalConnectedAccounts: 423,
    pendingInvites: 12,
    connectionPercentage: 78,
  });

  const [leaderboardData] = useState([
    {
      name: "John Smith",
      connectedAccounts: 45,
      connectionRate: "92%",
      avgConnectionTime: "2.3 days",
    },
    {
      name: "Sarah Johnson",
      connectedAccounts: 38,
      connectionRate: "88%",
      avgConnectionTime: "2.5 days",
    },
    {
      name: "Mike Wilson",
      connectedAccounts: 35,
      connectionRate: "85%",
      avgConnectionTime: "2.8 days",
    },
  ]);

  const spendingData = {
    labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
    datasets: [
      {
        label: "Monthly Spending Trends",
        data: [3000, 3500, 2800, 4200, 3800, 3600],
        borderColor: "rgb(75, 192, 192)",
        tension: 0.1,
      },
    ],
  };

  const categoryData = {
    labels: [
      "Housing",
      "Transportation",
      "Food",
      "Utilities",
      "Entertainment",
      "Other",
    ],
    datasets: [
      {
        data: [35, 20, 15, 10, 10, 10],
        backgroundColor: [
          "#FF6384",
          "#36A2EB",
          "#FFCE56",
          "#4BC0C0",
          "#9966FF",
          "#FF9F40",
        ],
      },
    ],
  };

  const [mockClients] = useState([
    {
      name: "Alice Johnson",
      status: "Connected",
      connectedAccounts: 3,
      lastActivity: "2024-03-15",
    },
    {
      name: "Bob Smith",
      status: "Pending",
      connectedAccounts: 0,
      lastActivity: "2024-03-14",
    },
    {
      name: "Carol Williams",
      status: "Not Connected",
      connectedAccounts: 0,
      lastActivity: "Never",
    },
  ]);

  const [mockAccounts] = useState([
    {
      name: "Checking Account",
      bank: "Chase",
      balance: 15000,
      status: "Active",
    },
    {
      name: "Savings Account",
      bank: "Wells Fargo",
      balance: 50000,
      status: "Active",
    },
    {
      name: "Credit Card",
      bank: "American Express",
      balance: 2500,
      status: "Error",
    },
  ]);

  const [mockExpenses] = useState([
    {
      vendor: "Netflix",
      frequency: "Monthly",
      amount: 15.99,
      category: "Entertainment",
    },
    {
      vendor: "Gym Membership",
      frequency: "Monthly",
      amount: 50.0,
      category: "Health",
    },
    {
      vendor: "Car Insurance",
      frequency: "Monthly",
      amount: 120.0,
      category: "Insurance",
    },
  ]);

  const [mockCreditScore] = useState({
    score: 750,
    trends: {
      change: 15,
      lastUpdate: "2024-03-15",
    },
  });

  return (
    <SubPageLayout
      breadcrumbItems={[
        { label: "Financial Planning", path: "/financial-planning" },
        { label: "Banking", path: "/financial-planning/banking" },
      ]}
      title="Banking"
      subtitle="Manage and track banking activities"
    >
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-8">
            Banking Dashboard
          </h1>

          <TabNavigation activeTab={activeTab} setActiveTab={setActiveTab} />

          {activeTab === "dashboard" && (
            <>
              <StatisticsPanel statistics={statistics} />
              <ClientSegmentation />
              <AdvisoryTips />
              <PriorityClients />
              <ValueProposition />
              <Leaderboard data={leaderboardData} />
              <ActionButtons />
            </>
          )}

          {activeTab === "spending" && (
            <>
              <ClientSearch />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                <SpendingTrends spendingData={spendingData} />
                <CategoryManager categoryData={categoryData} />
              </div>
              <BudgetTracker />
              <SpendingAlerts />
              <RecurringExpenses expenses={mockExpenses} />
            </>
          )}

          {activeTab === "accounts" && (
            <>
              <AccountHealthScore accounts={mockAccounts} />
              <AccountActions />
              <ClientBankingPanel accounts={mockAccounts} />
              <ConnectionQuality accounts={mockAccounts} />
              <CreditMonitoring
                creditScore={mockCreditScore.score}
                trends={mockCreditScore.trends}
              />
            </>
          )}

          {activeTab === "clients" && (
            <>
              <ClientStats
                stats={{
                  total: 150,
                  connected: 78,
                  pending: 42,
                  notConnected: 30,
                }}
              />
              <BulkActions />
              <ClientFilters />
              <ClientSearch />
              <ClientTable clients={mockClients} />
              {mockClients.map((client, index) => (
                <ClientDetails
                  key={index}
                  client={{
                    ...client,
                    id: `CL-${1000 + index}`,
                    email: "client@example.com",
                    phone: "(555) 123-4567",
                    address: "123 Main St, City, State 12345",
                    totalBalance: 150000,
                    recentActivity: [
                      { action: "Account Connected", date: "2 hours ago" },
                      { action: "Profile Updated", date: "2 days ago" },
                      { action: "Meeting Scheduled", date: "1 week ago" },
                    ],
                  }}
                />
              ))}
            </>
          )}

          {activeTab === "security" && (
            <>
              <SecurityStatus />
              <SecurityActivity />
              <SecuritySettings />
              <AccessControl />
            </>
          )}
        </div>
      </div>
    </SubPageLayout>
  );
};

export default Banking;
